import { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLastActiveGroup } from '@/hooks/useLastActiveGroup';
import { useGroupMembership } from '@/hooks/useGroupMembership';
import {
  fetchSeries,
  fetchLeaderboard,
  fetchUserBets,
  fetchGroupBets,
  fetchAllUserSideBetPicks,
  fetchSeriesPicksForGroup,
  fetchSeriesIdsWithOdds,
} from '@/lib/supabase-queries';
import type { UserSideBetPick } from '@/lib/supabase-queries';
import { PicksRevealSheet, type RevealPick, type PicksRevealOutcome } from '@/components/PicksRevealSheet';
import { supabase } from '@/integrations/supabase/client';
import { calculateClientLeaderboard } from '@/lib/scoring';
import { useDevMode } from '@/hooks/useDevMode';
import MainFeedHeader from '@/components/MainFeedHeader';
import Footer from '@/components/Footer';
import { SeriesListSkeleton } from '@/components/Skeletons';
import { LeaderboardPreview } from '@/components/LeaderboardPreview';
import { SeriesCarousel } from '@/components/SeriesCarousel';
import { RecentResultsCarousel } from '@/components/RecentResultsCarousel';
import { SideBetsCarousel } from '@/components/SideBetsCarousel';
import { MyBetsSummary } from '@/components/MyBetsSummary';
import { WaitingRoom, WaitingRoomMember } from '@/components/WaitingRoom';
import { PendingActionsBanner } from '@/components/PendingActionsBanner';
import { MemberBetStatusList } from '@/components/MemberBetStatusList';
import { FeedbackBanner } from '@/components/FeedbackBanner';

const GroupPage = () => {
  const { groupId } = useParams();
  const { user } = useAuth();
  const devMode = useDevMode();
  const { setLastActiveGroup } = useLastActiveGroup();
  const [group, setGroup] = useState<any>(null);
  const [seriesList, setSeriesList] = useState<any[]>([]);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [myBets, setMyBets] = useState<any[]>([]);
  const [mySideBetPicks, setMySideBetPicks] = useState<UserSideBetPick[]>([]);
  const [myLeaderboardTotal, setMyLeaderboardTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCommissionerUser, setIsCommissionerUser] = useState(false);
  const [waitingRoomMembers, setWaitingRoomMembers] = useState<WaitingRoomMember[]>([]);
  const [totalActiveSeries, setTotalActiveSeries] = useState(0);
  const [totalOpenSideBets, setTotalOpenSideBets] = useState(0);
  const [unpickedSideBetsCount, setUnpickedSideBetsCount] = useState(0);
  const [revealSeriesId, setRevealSeriesId] = useState<string | null>(null);
  const [revealPicks, setRevealPicks] = useState<RevealPick[]>([]);
  const [revealLoading, setRevealLoading] = useState(false);
  const [seriesIdsWithOdds, setSeriesIdsWithOdds] = useState<Set<string>>(new Set());
  const { isMember, loading: membershipLoading } = useGroupMembership(groupId, user?.id);

  const handleRevealSeries = async (seriesId: string) => {
    if (!groupId) return;
    setRevealSeriesId(seriesId);
    setRevealLoading(true);
    setRevealPicks([]);
    try {
      const picks = await fetchSeriesPicksForGroup(groupId, seriesId);
      const mapped: RevealPick[] = picks.map((p) => {
        const match = p.predicted_score.match(/(\d+)-(\d+)/);
        const label = match
          ? `${p.predicted_winner} in ${Number(match[1]) + Number(match[2])}`
          : p.predicted_winner;
        return {
          user_id: p.user_id,
          display_name: p.display_name,
          avatar_id: p.avatar_id,
          label,
          points_earned: p.points_earned,
          is_correct_winner: p.is_correct_winner,
          is_correct_score: p.is_correct_score,
        };
      });
      setRevealPicks(mapped);
    } catch (err) {
      console.warn('fetchSeriesPicksForGroup failed:', err);
    } finally {
      setRevealLoading(false);
    }
  };

  const revealSeries = revealSeriesId ? seriesList.find((s) => s.id === revealSeriesId) : null;

  // Derive resolved-series outcome for PicksRevealSheet (winner banner + bucket classification).
  // final_score is stored as "home-away" but displayed as "away-home" (matches SeriesCard).
  const revealOutcome: PicksRevealOutcome | undefined = (() => {
    if (!revealSeries) return undefined;
    if (revealSeries.status !== 'completed' || !revealSeries.winner) return undefined;
    const fs: string = revealSeries.final_score ?? '';
    const m = fs.match(/(\d+)-(\d+)/);
    const total = m ? Number(m[1]) + Number(m[2]) : 0;
    const displayScore = m ? `${m[2]}-${m[1]}` : fs;
    return {
      winner: revealSeries.winner,
      finalScore: displayScore,
      winningLabel: total > 0 ? `${revealSeries.winner} in ${total}` : revealSeries.winner,
    };
  })();

  // Fetch leaderboard and update state (used by initial load + Realtime subscription)
  const refreshLeaderboard = async (currentSeriesList: any[]) => {
    if (!groupId) return;
    const lb = await fetchLeaderboard(groupId);
    if (lb && lb.length > 0) {
      setLeaderboard(lb);
    } else {
      const [groupBets, { data: members }] = await Promise.all([
        fetchGroupBets(groupId),
        supabase.from('group_members').select('user_id, display_name').eq('group_id', groupId),
      ]);
      const memberNames: Record<string, string> = {};
      members?.forEach((m: any) => { if (m.user_id) memberNames[m.user_id] = m.display_name; });
      const clientLb = calculateClientLeaderboard(groupBets, currentSeriesList, memberNames);
      setLeaderboard(clientLb);
    }
  };

  useEffect(() => {
    if (!groupId || !user || !isMember) return;
    const load = async () => {
      setLoading(true);
      let g: any = null, s: any[] = [], lb: any = null, bets: any[] = [], groupBets: any[] = [], members: any[] | null = null;
      let sideBetPicks: UserSideBetPick[] = [];
      let memberBetCountsRaw: Array<{ user_id: string; series_bet_count: number; side_bet_pick_count: number }> = [];
      let oddsIds: Set<string> = new Set();
      try {
        const results = await Promise.all([
          supabase.from('groups').select('*').eq('id', groupId).single(),
          fetchSeries(),
          fetchLeaderboard(groupId),
          fetchUserBets(user.id, groupId),
          fetchGroupBets(groupId),
          supabase.from('group_members').select('user_id, display_name, role, avatar_id').eq('group_id', groupId),
          fetchAllUserSideBetPicks(user.id, groupId),
          supabase.rpc('get_group_member_bet_counts_v2', { p_group_id: groupId }),
          fetchSeriesIdsWithOdds(),
        ]);
        g = results[0].data;
        s = results[1];
        lb = results[2];
        bets = results[3];
        groupBets = results[4];
        members = results[5].data;
        sideBetPicks = results[6];
        memberBetCountsRaw = results[7].data ?? [];
        oddsIds = results[8];
      } catch (err) {
        console.warn('GroupPage load failed:', err);
        setLoading(false);
        return;
      }
      setGroup(g);
      if (g) setLastActiveGroup(groupId);
      setSeriesList(s);
      setMyBets(bets);
      setMySideBetPicks(sideBetPicks);
      setSeriesIdsWithOdds(oddsIds);

      // Extract this user's authoritative total from the leaderboard (includes
      // side bet points + champion bonus) for the group-page summary widget.
      const myLbEntry = (lb || []).find((e: any) => e.user_id === user.id);
      setMyLeaderboardTotal(
        typeof myLbEntry?.total_points === 'number' ? myLbEntry.total_points : null
      );

      // Check if current user is commissioner of this group
      const myMembership = members?.find((m: any) => m.user_id === user.id);
      setIsCommissionerUser(myMembership?.role === 'commissioner');

      // Build avatar map from group_members (source of truth for avatars)
      const avatarMap: Record<string, number | null> = {};
      const memberNames: Record<string, string> = {};
      members?.forEach((m: any) => {
        if (m.user_id) {
          memberNames[m.user_id] = m.display_name;
          avatarMap[m.user_id] = m.avatar_id ?? null;
        }
      });

      // Count open side bets + unpicked for this user
      let openSideBetsCount = 0;
      try {
        const { data: openSideBets } = await supabase
          .from('global_side_bets')
          .select('id')
          .eq('status', 'open');
        openSideBetsCount = openSideBets?.length ?? 0;
        if (openSideBets && openSideBets.length > 0) {
          const { data: userPicks } = await supabase
            .from('global_side_bet_picks')
            .select('side_bet_id')
            .eq('user_id', user.id)
            .eq('group_id', groupId);
          const pickedIds = new Set((userPicks || []).map((p: any) => p.side_bet_id));
          setUnpickedSideBetsCount(openSideBets.filter(b => !pickedIds.has(b.id)).length);
        } else {
          setUnpickedSideBetsCount(0);
        }
      } catch {
        setUnpickedSideBetsCount(0);
      }

      const activeSeries = s.filter((sr: any) => sr.status === 'upcoming' || sr.status === 'in_progress');
      const activeSeriesCount = activeSeries.length;
      setTotalActiveSeries(activeSeriesCount);
      setTotalOpenSideBets(openSideBetsCount);

      const memberSeriesCounts: Record<string, number> = {};
      const memberSideBetCounts: Record<string, number> = {};
      memberBetCountsRaw.forEach(({ user_id, series_bet_count, side_bet_pick_count }) => {
        memberSeriesCounts[user_id] = Number(series_bet_count) || 0;
        memberSideBetCounts[user_id] = Number(side_bet_pick_count) || 0;
      });

      const waitingRoomMemberList: WaitingRoomMember[] = (members ?? []).map((m: any) => ({
        user_id: m.user_id,
        display_name: m.display_name,
        avatar_id: m.avatar_id ?? null,
        seriesBetCount: memberSeriesCounts[m.user_id] || 0,
        sideBetPickCount: memberSideBetCounts[m.user_id] || 0,
      }));
      setWaitingRoomMembers(waitingRoomMemberList);

      if (lb && lb.length > 0) {
        // Merge avatar_id from group_members into leaderboard entries
        const enriched = lb.map((entry: any) => ({
          ...entry,
          avatar_id: avatarMap[entry.user_id] ?? entry.avatar_id ?? null,
        }));
        setLeaderboard(enriched);
      } else {
        const clientLb = calculateClientLeaderboard(groupBets, s, memberNames);
        setLeaderboard(clientLb);
      }

      setLoading(false);
    };
    load();
  }, [groupId, user, isMember]);

  // Realtime leaderboard subscription — re-fetch on any leaderboard change for this group
  useEffect(() => {
    if (!groupId) return;

    const channel = supabase
      .channel(`leaderboard:${groupId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leaderboard',
          filter: `group_id=eq.${groupId}`,
        },
        async () => {
          await refreshLeaderboard(seriesList);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [groupId, seriesList]);

  // Realtime own-bets subscription — keeps myBets fresh so NEW badge / pending-bet
  // pills react immediately when the user submits a pick on a child route and
  // navigates back. Without this, GroupPage stays mounted with stale state until
  // a hard refresh.
  useEffect(() => {
    if (!groupId || !user) return;

    const channel = supabase
      .channel(`bets:${groupId}:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bets',
          filter: `user_id=eq.${user.id}`,
        },
        async () => {
          const fresh = await fetchUserBets(user.id, groupId);
          setMyBets(fresh);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [groupId, user]);

  // Belt-and-suspenders: refetch myBets on window focus. Realtime can drop on
  // mobile browsers when the tab backgrounds during a pick flow; focus refresh
  // is a cheap safety net.
  useEffect(() => {
    if (!groupId || !user) return;
    const onFocus = async () => {
      const fresh = await fetchUserBets(user.id, groupId);
      setMyBets(fresh);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [groupId, user]);

  const refreshGroup = async () => {
    if (!groupId) return;
    const { data: g } = await supabase.from('groups').select('*').eq('id', groupId).single();
    setGroup(g);
  };

  // Non-member: redirect to home (will show NoGroupsPage or their actual group)
  if (!membershipLoading && isMember === false) {
    return <Navigate to="/" replace />;
  }

  if (loading || membershipLoading) {
    return (
      <div className="min-h-screen bg-background">
        <MainFeedHeader groupId={groupId!} groupName="טוען..." isCommissioner={false} />
        <main className="pt-14 flex-1">
          <div className="container mx-auto px-4 py-6">
            <SeriesListSkeleton />
          </div>
        </main>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-background">
        <MainFeedHeader groupId={groupId!} groupName="שגיאה" isCommissioner={false} />
        <main className="pt-14 flex-1">
          <div className="container mx-auto px-4 py-20 text-center text-muted-foreground">
            קבוצה לא נמצאה
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col font-leon">
      <MainFeedHeader groupId={groupId!} groupName={group.name} isCommissioner={isCommissionerUser} />

      <main className="pt-14 flex-1">
        <div className="container mx-auto px-4 py-6 space-y-6">

          <FeedbackBanner
            winnerNames={(() => {
              const top = leaderboard.reduce((m, e) => Math.max(m, e.total_points ?? 0), 0);
              if (top <= 0) return [];
              // All members tied at the top score are champions (ranking breaks
              // point ties arbitrarily, so congratulate every co-leader).
              return leaderboard
                .filter((e) => (e.total_points ?? 0) === top)
                .map((e) => e.display_name);
            })()}
          />

          {(() => {
            const playoffStarted = seriesList.some(s => s.status === 'in_progress' || s.status === 'completed');
            const hasActiveOrCompleted = devMode || playoffStarted;
            const hasUserBetsOrActivity =
              devMode || myBets.length > 0 || mySideBetPicks.length > 0 || hasActiveOrCompleted;
            const showMyBets = hasUserBetsOrActivity;

            // Compute unbetted series count for pending actions banner
            const bettedSeriesIds = new Set(myBets.map(b => b.series_id));
            const upcomingSeries = seriesList.filter(s => s.status === 'upcoming');
            const unbettedSeriesCount = upcomingSeries.filter(s => !bettedSeriesIds.has(s.id)).length;

            // Exit pre-playoff mode once any series is seeded (upcoming) or underway
            const hasUpcomingSeries = upcomingSeries.length > 0;
            if (!hasActiveOrCompleted && !hasUpcomingSeries && !devMode) {
              // Pre-playoff: WaitingRoom + BracketPreview + side bets
              return (
                <>
                  <PendingActionsBanner
                    unbettedSeriesCount={unbettedSeriesCount}
                    unpickedSideBetsCount={unpickedSideBetsCount}
                    groupId={groupId!}
                    prePlayoff
                    countdownTarget="2026-04-19T15:30:00Z"
                  />
                  <WaitingRoom
                    members={waitingRoomMembers}
                    groupId={groupId!}
                    totalActiveSeries={totalActiveSeries}
                    totalOpenSideBets={totalOpenSideBets}
                    isCommissioner={isCommissionerUser}
                    inviteCode={group.invite_code}
                    groupName={group.name}
                    inviteRestricted={(group.settings as Record<string, unknown>)?.invite_restricted === true}
                    paymentLink={group.payment_link}
                    chatLink={group.chat_link}
                  />
                  <SideBetsCarousel groupId={groupId!} />
                  {isCommissionerUser && (
                    <MemberBetStatusList
                      groupId={groupId!}
                      seriesList={seriesList}
                      footerLink={{ label: 'הגדרות נוספות \u2190', to: `/group/${groupId}/manage` }}
                    />
                  )}
                </>
              );
            }

            return (
              <>
                <PendingActionsBanner
                  unbettedSeriesCount={unbettedSeriesCount}
                  unpickedSideBetsCount={unpickedSideBetsCount}
                  groupId={groupId!}
                />
                {(devMode || !playoffStarted) && (
                  <WaitingRoom
                    members={waitingRoomMembers}
                    groupId={groupId!}
                    totalActiveSeries={totalActiveSeries}
                    totalOpenSideBets={totalOpenSideBets}
                    isCommissioner={isCommissionerUser}
                    inviteCode={group.invite_code}
                    groupName={group.name}
                    inviteRestricted={(group.settings as Record<string, unknown>)?.invite_restricted === true}
                    paymentLink={group.payment_link}
                    chatLink={group.chat_link}
                  />
                )}
                {showMyBets && (
                  <MyBetsSummary
                    bets={myBets}
                    seriesList={seriesList}
                    groupId={groupId!}
                    sideBetPicks={mySideBetPicks}
                    leaderboardTotal={myLeaderboardTotal}
                  />
                )}
                {hasActiveOrCompleted && (
                  <LeaderboardPreview entries={leaderboard} currentUserId={user?.id} groupId={groupId!} />
                )}
                <div id="series-section">
                  <SeriesCarousel
                    seriesList={seriesList}
                    groupId={groupId!}
                    userBets={myBets}
                    seriesIdsWithOdds={seriesIdsWithOdds}
                    onRevealClick={handleRevealSeries}
                  />
                </div>
                <RecentResultsCarousel
                  seriesList={seriesList}
                  groupId={groupId!}
                  userBets={myBets}
                  onRevealClick={handleRevealSeries}
                />
                <SideBetsCarousel groupId={groupId!} />
                {isCommissionerUser && (
                  <MemberBetStatusList
                    groupId={groupId!}
                    seriesList={seriesList}
                    footerLink={{ label: 'הגדרות נוספות \u2190', to: `/group/${groupId}/manage` }}
                  />
                )}
              </>
            );
          })()}

        </div>

      </main>
      <Footer />
      <PicksRevealSheet
        open={revealSeriesId !== null}
        onClose={() => setRevealSeriesId(null)}
        title={revealSeries ? `${revealSeries.team_home} vs ${revealSeries.team_away}` : ''}
        subtitle={revealLoading ? 'טוען...' : 'בחירות חברי הקבוצה'}
        picks={revealPicks}
        currentUserId={user?.id}
        totalMembers={waitingRoomMembers.length}
        outcome={revealOutcome}
      />
    </div>
  );
};

export default GroupPage;
